import React from 'react';
import { Trophy } from 'lucide-react';
import ChartCard from './ChartCard';

const Stat = ({ label, value, testId }) => (
  <div>
    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="font-mono text-lg font-bold text-autoxium-blue" data-testid={testId}>{value}</p>
  </div>
);

/**
 * Results Summary (§2): the first thing shown after Submit — every value read directly
 * off FullSimulationResponse.summary, nothing recalculated client-side.
 */
const ResultsSummaryPanel = ({ summary }) => {
  if (!summary) return null;

  return (
    <ChartCard icon={Trophy} title="Results Summary" testId="simulator-results-summary">
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="Max. Vehicle Speed" value={`${summary.max_vehicle_speed_kph.toFixed(1)} km/h`} testId="summary-max-speed" />
        <Stat label="Max. Wheel Torque" value={`${summary.max_wheel_torque_nm.toFixed(0)} Nm`} testId="summary-max-wheel-torque" />
        <Stat label="Max. Wheel Traction Power" value={`${summary.max_wheel_traction_power_kw.toFixed(1)} kW`} testId="summary-max-wheel-power" />
        <Stat label="Estimated Range (Test Mass)" value={`${summary.estimated_range_km.toFixed(1)} km`} testId="summary-estimated-range" />
        <Stat label="Energy Consumption (Test Mass)" value={`${summary.energy_consumption_wh_per_km.toFixed(1)} Wh/km`} testId="summary-energy-consumption" />
        {summary.estimated_range_full_load_km != null && (
          <Stat
            label="Range Fully Loaded (GVW)"
            value={`${summary.estimated_range_full_load_km.toFixed(1)} km (${summary.energy_consumption_full_load_wh_per_km.toFixed(0)} Wh/km)`}
            testId="summary-estimated-range-full-load"
          />
        )}
        <Stat label="Simulated Run Time" value={`${summary.actual_simulation_time_s.toFixed(0)} s`} testId="summary-actual-simulation-time" />
        <Stat
          label="0-100 km/h (Sized Motor)"
          value={summary.simulated_accel_time_s == null
            ? 'Not reached'
            : `${summary.simulated_accel_time_s.toFixed(2)} s (target ${summary.accel_time_target_s.toFixed(1)} s)`}
          testId="summary-accel-time"
        />
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Range and energy are simulated from Maximum to Minimum Battery SoC at the test mass
        {summary.test_mass_kg ? ` (${summary.test_mass_kg.toFixed(0)} kg: Vehicle Mass + 100 kg, capped at Maximum Payload)` : ''} —
        the mass certified WLTP / MIDC ranges and published 0-100 km/h times are measured at — and
        include motor, inverter, driveline, auxiliary, and battery internal-resistance losses, net of
        regenerative braking. The fully-loaded figure uses the same pack at Vehicle Mass + Maximum Payload.
      </p>
    </ChartCard>
  );
};

export default ResultsSummaryPanel;
